import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as Sentry from '@sentry/vue'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  applyActionCode,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  type User
} from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { api, setTokenGetter } from '@/lib/api'

// Check if we're in dev bypass mode
const isDevBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'

// Mock user for dev bypass
interface DevUser {
  email: string
  uid: string
  emailVerified: boolean
  getIdToken: () => Promise<string>
  reload: () => Promise<void>
}

const devUser: DevUser = {
  email: 'dev@localhost',
  uid: 'dev-user-local-bypass',
  emailVerified: true,
  getIdToken: async () => 'dev-bypass-token',
  reload: async () => {}
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | DevUser | null>(null)
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  /** staffRole from the last /auth/sync response. null = regular user. */
  const staffRole = ref<string | null>(null)

  const isAuthenticated = computed(() => !!user.value)
  const isEmailVerified = computed(() => {
    if (!user.value) return false
    if (isDevBypass) return true
    return (user.value as User).emailVerified === true
  })
  /** True for any internal staff member (any staffRole value). */
  const isStaff = computed(() => staffRole.value !== null)
  
  // Get the current user's ID token for API calls
  async function getToken(): Promise<string | null> {
    if (!user.value) return null
    return user.value.getIdToken()
  }

  // Register token getter with API client
  setTokenGetter(getToken)

  // Initialize auth state listener
  function initAuth(): Promise<void> {
    return new Promise((resolve) => {
      if (isInitialized.value) {
        resolve()
        return
      }

      // DEV BYPASS: Skip Firebase, use mock user
      if (isDevBypass) {
        console.log('🔧 Dev auth bypass enabled - using mock user')
        user.value = devUser
        Sentry.setUser({ id: devUser.uid, email: devUser.email })
        isInitialized.value = true
        resolve()
        return
      }

      // Production: Normal Firebase auth
      if (!auth) {
        console.error('Firebase auth not initialized')
        isInitialized.value = true
        resolve()
        return
      }

      // Safety net: if Firebase never fires (network failure, mobile timeout),
      // resolve as unauthenticated after 10s to avoid a permanent blank page.
      const timeout = setTimeout(() => {
        if (!isInitialized.value) {
          console.error('Firebase auth timed out - treating as unauthenticated')
          isInitialized.value = true
          resolve()
        }
      }, 10000)

      onAuthStateChanged(auth, async (firebaseUser) => {
        clearTimeout(timeout)
        user.value = firebaseUser

        if (firebaseUser) {
          Sentry.setUser({ id: firebaseUser.uid, email: firebaseUser.email ?? undefined })
        } else {
          Sentry.setUser(null)
        }

        // If user just logged in, ensure they exist in our database
        if (firebaseUser) {
          try {
            const syncData = await api.post<{ staffRole?: string | null }>('/auth/sync', {})
            staffRole.value = syncData?.staffRole ?? null

            // After sync, claim a pending invite token if the user arrived via an invite link.
            // AcceptInviteView stores the token in sessionStorage before redirecting to /register.
            const pendingToken = sessionStorage.getItem('pendingInviteToken')
            if (pendingToken) {
              try {
                await api.post('/auth/accept-invite', { token: pendingToken })
              } catch (e) {
                console.error('Failed to claim invite token:', e)
              } finally {
                sessionStorage.removeItem('pendingInviteToken')
              }
            }
          } catch (e) {
            console.error('Failed to sync user with backend:', e)
          }
        }

        isInitialized.value = true
        resolve()
      })
    })
  }

  async function login(email: string, password: string) {
    // DEV BYPASS
    if (isDevBypass) {
      user.value = devUser
      return
    }

    if (!auth) {
      throw new Error('Firebase auth not initialized')
    }

    isLoading.value = true
    error.value = null

    try {
      const credential = await signInWithEmailAndPassword(auth, email, password)
      user.value = credential.user
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Login failed'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function register(email: string, password: string) {
    // DEV BYPASS
    if (isDevBypass) {
      user.value = devUser
      return
    }

    if (!auth) {
      throw new Error('Firebase auth not initialized')
    }

    isLoading.value = true
    error.value = null

    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password)
      // Set user immediately after successful account creation
      user.value = credential.user
      // Attempt to send verification email, but do not fail registration
      if (!credential.user.emailVerified) {
        try {
          await sendEmailVerification(credential.user)
        } catch (verificationError) {
          // Optionally record a non-fatal error while allowing the user to proceed
          if (verificationError instanceof Error) {
            console.error('Failed to send verification email:', verificationError)
            error.value = verificationError.message
          } else {
            console.error('Failed to send verification email:', verificationError)
          }
        }
      }
    } catch (e: any) {
      let friendlyMessage = 'Registration failed';
      if (e && typeof e === 'object') {
        // Firebase v9+ error code
        if (e.code === 'auth/email-already-in-use') {
          friendlyMessage = 'Sorry, that email address is already in use.';
        }
      }
      error.value = friendlyMessage;
      throw new Error(friendlyMessage);
    } finally {
      isLoading.value = false
    }
  }

  async function loginWithGoogle(): Promise<boolean> {
    if (isDevBypass) {
      user.value = devUser
      return true
    }

    if (!auth) throw new Error('Firebase auth not initialized')

    isLoading.value = true
    error.value = null

    try {
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({ prompt: 'select_account' })
      const credential = await signInWithPopup(auth, provider)
      user.value = credential.user
      return true
    } catch (e: any) {
      if (e.code === 'auth/popup-closed-by-user' || e.code === 'auth/cancelled-popup-request') {
        // User dismissed the popup - not an error worth surfacing
        return false
      }
      if (e.code === 'auth/account-exists-with-different-credential') {
        const msg = 'An account with this email already exists. Please sign in with your email and password.'
        error.value = msg
        throw new Error(msg)
      }
      error.value = e instanceof Error ? e.message : 'Google sign-in failed'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function logout() {
    // DEV BYPASS
    if (isDevBypass) {
      user.value = null
      Sentry.setUser(null)
      return
    }

    if (!auth) {
      throw new Error('Firebase auth not initialized')
    }

    isLoading.value = true
    error.value = null

    try {
      await signOut(auth)
      user.value = null
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Logout failed'
      throw e
    } finally {
      isLoading.value = false
    }
  }

  // Resend verification email
  async function resendVerification(): Promise<void> {
    if (!user.value || isDevBypass) return
    const firebaseUser = user.value as User
    if (firebaseUser.emailVerified) return
    await sendEmailVerification(firebaseUser)
  }

  // Reload user to pick up emailVerified status from Firebase
  async function refreshUser(): Promise<boolean> {
    if (!user.value || isDevBypass) return true
    const firebaseUser = user.value as User
    await firebaseUser.reload()
    // Force reactivity update by re-assigning
    user.value = auth!.currentUser
    // If email just became verified, sync with the backend so it can send the
    // welcome email (which is withheld until verification is confirmed).
    if (firebaseUser.emailVerified) {
      try { await api.post('/auth/sync', {}) } catch { /* non-fatal */ }
    }
    return firebaseUser.emailVerified
  }

  const actionCodeSettings = {
    url: window.location.origin + '/login',
    handleCodeInApp: true,
  }

  async function sendPasswordReset(email: string) {
    if (isDevBypass) return
    if (!auth) throw new Error('Firebase auth not initialized')
    await sendPasswordResetEmail(auth, email, actionCodeSettings)
  }

  async function verifyEmailFromLink(oobCode: string): Promise<void> {
    if (!auth) throw new Error('Firebase auth not initialized')
    await applyActionCode(auth, oobCode)
    await refreshUser()
  }

  return {
    user,
    isInitialized,
    isLoading,
    error,
    isAuthenticated,
    isEmailVerified,
    staffRole,
    isStaff,
    getToken,
    initAuth,
    login,
    loginWithGoogle,
    register,
    logout,
    resendVerification,
    refreshUser,
    sendPasswordReset,
    verifyEmailFromLink
  }
})
